import { baseURL } from "app/baseUrl";
import type { HeaderNode } from "components/Contentful/RichTextRenderer";
import { cleanTitle } from "components/Contentful/RichTextRenderer/RichTextRenderer.utils";
import type { BlogV2Tag, BlogV2TagParsed } from "types/blog-v2-types";
import { siteUrl } from "utils/getSiteUrl";

export const formatDate = (date: Date): string => {
  const dateToParse = new Date(date);
  const parsedDay = dateToParse.getDate();
  return `${parsedDay} ${dateToParse.toLocaleString("en-US", {
    month: "short"
  })} ${dateToParse.getFullYear()}`;
};

export const getId = (node: HeaderNode): string => {
  return node.content?.length ? cleanTitle(node.content[0]?.value) : "";
};

// This function determines whether to open a link in a new window or the same as Contentful doesn't allow for easy customisation. Was originally just "#" but the prepending of /us/ means US links didn't work without explicitly declaring it.
export const checkLinkTarget = (link: string): string => {
  if (link.startsWith("#") || link.startsWith(`${siteUrl}/us/legal`))
    return "_self";
  return "_blank";
};

export const postsPerPage = 4;
export const categoryPostsPerPage = 8;

type FilteredItemsObject = {
  itemsToShow: number[];
  items: number[];
};

export const filterItemsToShow = (
  currentPage: number,
  totalItems: number,
  itemsPerPage: number
): FilteredItemsObject => {
  const pagesToInclude = Math.ceil(totalItems / itemsPerPage);
  const items = Array.from({ length: pagesToInclude }, (_, i) => i + 1);
  let itemsToShow = items;
  if (items.length > 5) {
    let start = 0;
    if (currentPage > 2) {
      start = currentPage - 3;
    }
    if (items.length - currentPage < 3) {
      start = items.length - 5;
    }
    itemsToShow = items.slice(start, start + 5);
  }
  return { itemsToShow, items };
};

export const parseTagData = (tag: BlogV2Tag): BlogV2TagParsed => {
  return {
    title: tag.fields.title,
    slug: tag.fields.slug,
    introMetaDescription: tag.fields.introMetaDescription,
    toNoIndex: tag.fields.toNoIndex ?? false
  };
};

export const parseTagsData = (tags: BlogV2Tag[]): BlogV2TagParsed[] => {
  return tags.map((tag) => parseTagData(tag));
};

export const blogSlug = "/blog";

const exists = <T>(arg: T): arg is NonNullable<T> => !!arg;
export const extractEntries = (entryArray) => {
  return entryArray
    .map((entry) => {
      return entry?.fields ?? null;
    })
    .filter(exists);
};

type PrevNextLinks = {
  rel: string;
  url: string;
};

type PrevNextProps = (
  currentPage: number,
  totalPosts: number,
  postsOnPage: number,
  slugPrefix: string,
  extraPost?: boolean
) => PrevNextLinks[];

export const generatePrevNext: PrevNextProps = (
  currentPage,
  totalPosts,
  postsOnPage,
  slugPrefix,
  extraPost
) => {
  const relLinks = [] as PrevNextLinks[];
  let lastPage = Math.ceil(totalPosts / postsOnPage);
  if (extraPost) {
    lastPage = Math.ceil((totalPosts + 1) / postsOnPage);
  }
  if (+currentPage === 2) {
    const relLink = {
      rel: "prev",
      url: `${slugPrefix}`
    };
    relLinks.push(relLink);
  } else if (+currentPage > 2) {
    const relLink = {
      rel: "prev",
      url: `${slugPrefix}/page/${+currentPage - 1}`
    };
    relLinks.push(relLink);
  }
  if (+currentPage !== lastPage) {
    const nextLink = {
      rel: "next",
      url: `${slugPrefix}/page/${+currentPage + 1}`
    };
    relLinks.push(nextLink);
  }
  return relLinks;
};

type AlternatePaginatedProps = (
  thisLocale: "en-US" | "en-GB",
  otherLocale: "en-US" | "en-GB",
  thisSlug: string,
  otherSlug: string
) => Record<string, string>;

export const generateAlternatePaginated: AlternatePaginatedProps = (
  thisLocale,
  otherLocale,
  thisSlug,
  otherSlug
) => {
  const alternateUrls = {
    [thisLocale]: `${baseURL(thisLocale)}${thisSlug}`,
    [otherLocale]: `${baseURL(otherLocale)}${otherSlug}`
  };
  return alternateUrls;
};
